import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../../context/AuthContext';
import { useCreditHistory } from '../../hooks/useCreditHistory';

const SettingsFinances = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { t, i18n } = useTranslation('settings');
  const { history, loading } = useCreditHistory({ periodDays: '90' });

  const purchaseRows = useMemo(() => {
    const targetTypes = new Set(['PURCHASE', 'SUBSCRIPTION']);
    return [...(history || [])]
      .filter((tx) => targetTypes.has((tx.transaction_type || '').toUpperCase()) || Number(tx.amount) > 0)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 8);
  }, [history]);

  const formatAmount = (amount) => {
    if (amount == null) return '—';
    const signed = Number(amount) > 0 ? `+${amount}` : `${amount}`;
    return `${signed} cr`;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '—';
    try {
      return new Date(timestamp).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch (_err) {
      return timestamp;
    }
  };

  const getTypeLabel = (type) => t(`finances.types.${type || 'DEFAULT'}`, { defaultValue: type?.replace(/_/g, ' ') || '—' });

  const handleViewHistory = () => {
    navigate('/history');
  };

  const handleRecharge = () => {
    navigate('/credits');
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-gray-800 pb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{t('finances.title')}</h2>
          <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">
            {t('finances.subtitle')}
          </p>
        </div>
        <div className="hidden sm:block">
          <div className="h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
            <FontAwesomeIcon icon="receipt" className="text-xl" />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 sm:grid-cols-2">
        <button
          onClick={handleRecharge}
          className="group rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-6 text-left transition-all hover:border-indigo-300 dark:hover:border-indigo-500/50 hover:shadow-md"
        >
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform">
              <FontAwesomeIcon icon="coins" className="text-xl" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white">{t('finances.rechargeCredits')}</h3>
              <p className="text-sm text-slate-500 dark:text-gray-400">{t('finances.rechargeDesc')}</p>
            </div>
          </div>
        </button>

        <button
          onClick={handleViewHistory}
          className="group rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-6 text-left transition-all hover:border-violet-300 dark:hover:border-violet-500/50 hover:shadow-md"
        >
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 dark:text-violet-400 group-hover:scale-110 transition-transform">
              <FontAwesomeIcon icon="clock-rotate-left" className="text-xl" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white">{t('finances.transactionHistory')}</h3>
              <p className="text-sm text-slate-500 dark:text-gray-400">{t('finances.transactionDesc')}</p>
            </div>
          </div>
        </button>
      </div>

      {/* Summary Card */}
      <div className="rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-6">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-gray-400 mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon="chart-line" className="text-slate-400" />
          {t('finances.financialSummary')}
        </h3>
        
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl bg-slate-50 dark:bg-gray-800/50 p-4">
            <p className="text-xs text-slate-500 dark:text-gray-400 mb-1">{t('finances.currentCredits')}</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">
              {userProfile?.saas_usage?.credits_remaining ?? 0}
            </p>
          </div>
          
          <div className="rounded-xl bg-slate-50 dark:bg-gray-800/50 p-4">
            <p className="text-xs text-slate-500 dark:text-gray-400 mb-1">{t('finances.totalCreditsSpent')}</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">
              {userProfile?.saas_usage?.credits_spent_total ?? 0}
            </p>
          </div>
          
          <div className="rounded-xl bg-slate-50 dark:bg-gray-800/50 p-4">
            <p className="text-xs text-slate-500 dark:text-gray-400 mb-1">{t('finances.jobsCompleted')}</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">
              {userProfile?.saas_usage?.usage_count ?? 0}
            </p>
          </div>
        </div>
      </div>

      {/* Purchases table */}
      <div className="rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('finances.purchasesTitle')}</h3>
            <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">{t('finances.purchasesSubtitle')}</p>
          </div>
          <span className="text-xs font-medium px-3 py-1 rounded-full bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300">
            {t('finances.recent', { count: purchaseRows.length })}
          </span>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-gray-800">
          <div className="grid grid-cols-12 bg-slate-50 dark:bg-slate-900/60 text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wide px-4 py-3">
            <div className="col-span-4">{t('finances.table.date')}</div>
            <div className="col-span-3">{t('finances.table.type')}</div>
            <div className="col-span-3">{t('finances.table.details')}</div>
            <div className="col-span-2 text-right">{t('finances.table.amount')}</div>
          </div>

          {purchaseRows.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-900">
              {loading ? t('finances.loading') : t('finances.emptyHistory')}
            </div>
          ) : (
            <div className="divide-y divide-slate-200 dark:divide-gray-800 bg-white dark:bg-slate-900">
              {purchaseRows.map((tx) => {
                const isPositive = Number(tx.amount) > 0;
                return (
                  <div
                    key={tx.id}
                    className="grid grid-cols-12 items-center px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                  >
                    <div className="col-span-4 font-medium text-slate-900 dark:text-white">{formatDateTime(tx.timestamp)}</div>
                    <div className="col-span-3 flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${isPositive ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                      <span>{getTypeLabel(tx.transaction_type)}</span>
                    </div>
                    <div className="col-span-3 text-slate-500 dark:text-slate-400 truncate" title={tx.description}>{tx.description || t('finances.noDescription')}</div>
                    <div className={`col-span-2 text-right font-semibold tabular-nums ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400'}`}>
                      {formatAmount(tx.amount)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Stripe Info */}
      {userProfile?.stripe?.customer_id && (
        <div className="rounded-2xl border border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-900/30 p-6">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-gray-400 mb-4 flex items-center gap-2">
            <FontAwesomeIcon icon="credit-card" className="text-slate-400" />
            {t('finances.paymentInfo')}
          </h3>
          
          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b border-slate-200 dark:border-gray-800">
              <span className="text-slate-600 dark:text-gray-400">{t('finances.stripeCustomerId')}</span>
              <span className="font-mono text-xs text-slate-900 dark:text-white bg-slate-100 dark:bg-gray-800 px-2 py-1 rounded">
                {userProfile.stripe.customer_id}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsFinances;
